//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { MediaFilterParamKey } from '@apis/content-library/contentLibraryApiModel';
import { ILabelIndexObject, LabelsSortField } from '@apis/gatewayMediaApiModel';
import LabeledCheckbox from '@components/atoms/labeled-checkbox/LabeledCheckbox';
import { BodyText, SubtitleText } from '@components/atoms/text/Text';
import { ClearSelectionButton } from '@components/media-filters/ClearSelectionButton';
import { ErrorMessage } from '@components/media-filters/ErrorMessage';
import {
  FilterSection,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { Modal } from '@components/molecules/modal/Modal';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import { ManageLabelsDialog } from '@content-drawer/filters/labels/ManageLabelsDialog';
import { useAllLabelsQuery } from '@content-drawer/filters/labels/hooks/useAllLabelsQuery';
import { sortLabels } from '@content-drawer/filters/labels/utils/sortLabels';
import { findOptionByLabel } from '@content-drawer/filters/labelsUtils';
import { getIsLabelManagementEnabled } from '@content-drawer/redux/getContentDrawer';
import { Mixins } from '@theme/mixins';
import { getFuzzySearch } from '@utils/getFuzzySearch';

export interface AllLabelsPanelProps {
  isOpen: boolean;
  onChange: (
    sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => void;
  onClose: () => void;
  section: FilterSection<string>;
}

export const AllLabelsPanel = ({
  isOpen,
  onChange,
  onClose,
  section,
}: AllLabelsPanelProps) => {
  const [options, setOptions] = useState(structuredClone(section.options));
  const [searchTerm, setSearchTerm] = useState('');
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const isLabelManagementEnabled = useSelector(getIsLabelManagementEnabled);

  const { data: response, error, isFetching } = useAllLabelsQuery();
  const allLabels = response?.data.indexObjects ?? [];
  const totalCount = response?.data.count ?? 0;

  // If we have a searchQuery, use the fuzzy based filtered labels, otherwise show `allLabels`
  const filteredLabels =
    searchTerm.length > 0
      ? getFuzzySearch({
          itemsToSearch: allLabels, // We are searching labels on the frontend side and do not need to track the search term.
          searchKeys: ['userTag'],
          searchTerm,
        })
      : allLabels;

  const labels = sortLabels(filteredLabels, {
    field: LabelsSortField.ItemsWithTag,
    isAscending: false,
  });

  const handleSelect = (
    labelObject: ILabelIndexObject,
    isSelected: boolean,
  ) => {
    const label = {
      count: labelObject.itemsWithTag,
      value: labelObject.userTag,
    };
    const selectedOption = findOptionByLabel(options, label);

    // If the list of already used options contains the currently selected label, set the `isSelected` state, otherwise add the current label to the end of the list of options.
    if (selectedOption) {
      selectedOption.isSelected = isSelected;
      setOptions((options) => [...options]);
    } else {
      const option = {
        isSelected,
        label: label.value,
        value: label.value,
      };
      setOptions((options) => [...options, option]);
    }
  };

  const isChecked = (labelObject: ILabelIndexObject) => {
    const label = {
      count: labelObject.itemsWithTag,
      value: labelObject.userTag,
    };
    const option = findOptionByLabel(options, label);
    return !!option?.isSelected;
  };

  const applyLabelsSelection = () => {
    const updatedSections = new Map<
      MediaFilterParamKey,
      FilterSection<FilterValue>
    >();
    updatedSections.set(section.paramKey, {
      ...section,
      // Filter out any options that represent lables that no longer exist
      // This may need to happen if the user deletes or updates a label
      options: options.filter((label) =>
        allLabels.some((labelObj) => labelObj.userTag === label.value),
      ),
    });
    onChange(updatedSections);
    onClose();
  };

  return (
    <Modal
      closeModal={onClose}
      contents={
        <>
          {!isManageModalOpen && (
            <>
              {totalCount > 0 && (
                <SearchBar
                  autoFocus={true}
                  handleClear={() => setSearchTerm('')}
                  isDisabled={allLabels.length === 0}
                  onChange={setSearchTerm}
                  placeholder='Search'
                  trackingIdPrefix={ContentDrawerTrackingIds.SEARCH_LABELS}
                  value={searchTerm}
                  visuallyHiddenLabel='Find labels'
                />
              )}
              <LabelsPanel>
                {!isFetching && (
                  <>
                    {error && <ErrorMessage error={error.message} />}
                    {allLabels.length >= 0 &&
                      searchTerm.length > 0 &&
                      filteredLabels.length === 0 && (
                        <LabelsLoaded>
                          No labels found. Adjust your search and try again.
                        </LabelsLoaded>
                      )}
                    {!error && allLabels.length === 0 && (
                      <LabelsLoaded>No labels found.</LabelsLoaded>
                    )}
                    {!error && allLabels.length > 0 && (
                      <StyledClearSelectionButton
                        handleClearSection={(options) => setOptions(options)}
                        options={options}
                      />
                    )}
                  </>
                )}
                <Labels>
                  {labels.map((label) => (
                    <Label
                      key={label.userTag}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation(); // Prevent toggle checkbox twice when click on label
                        handleSelect(label, !isChecked(label));
                      }}
                      title={label.userTag}
                    >
                      <LabeledCheckbox
                        handleClick={() => {
                          return; // Prevent default behaviour from checkbox component
                        }}
                        isChecked={isChecked(label)}
                        label={label.userTag}
                      />
                      <BodyText>({label.itemsWithTag})</BodyText>
                    </Label>
                  ))}
                </Labels>
              </LabelsPanel>
            </>
          )}
        </>
      }
      isModalOpen={isOpen}
      primaryButtonInfo={{
        buttonContent: 'Apply',
        onClick: applyLabelsSelection,
        trackingId: ContentDrawerTrackingIds.APPLY_LABELS,
      }}
      secondaryButtonInfo={{
        buttonContent: 'Cancel',
        onClick: onClose,
        trackingId: ContentDrawerTrackingIds.CLOSE_ALL_LABELS_MODAL,
      }}
      title={
        <H1>
          <SubtitleText>All Labels</SubtitleText>
          {isLabelManagementEnabled && (
            <ManageLabelsDialog
              onToggleModal={setIsManageModalOpen}
              shouldHideOverlay={true}
            />
          )}
        </H1>
      }
      width='wide'
    />
  );
};

const H1 = styled.h1(
  ({ theme }) => `
    display: flex;
    justify-content: start;
    align-items: center;
    gap: ${theme.spacings[2]};
  `,
);

const LabelsPanel = styled.div(
  ({ theme }) => `
    height: ${theme.mediumMeasure};
    padding: ${theme.spacings[2]} ${theme.spacings[3]};
    overflow-y: auto;
  `,
);

const StyledClearSelectionButton = styled(ClearSelectionButton<string>)(
  ({ theme }) => `
  margin-bottom: ${theme.spacings[2]};
  `,
);

const Labels = styled.ul(
  ({ theme }) => `
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: ${theme.spacings[2]};
    align-items: start;
    padding: ${theme.spacings[0]};
    list-style: none;
  `,
);

const Label = styled.li(
  ({ theme }) => `
    display: flex;
    flex-direction: row;
    justify-content: start;
    align-items: center;
    gap: ${theme.spacings[1]};
    cursor: pointer;
  `,
);

const LabelsLoaded = styled.div(
  ({ theme }) => `
    ${Mixins.Text.Metatag};
    width: 100%;
    padding: ${theme.spacings[6]};
    text-align: center;
`,
);
